import { NavLink } from 'react-router-dom'
import {
  Home,
  Layers,
  GraduationCap,
  Newspaper,
  Server,
  MoreHorizontal,
  Settings,
  ListChecks,
  type LucideIcon,
} from 'lucide-react'
import { useState, useRef, useEffect } from 'react'
import { useIsMobile } from '@/shared/hooks/useIsMobile'
import { useMobileFullscreen } from '@/shared/hooks/useMobileFullscreen'
import { useAuth } from '@/shared/hooks/useAuth'
import type { UserRole } from '@/shared/lib/auth'

interface TabDef {
  name: string
  shortName: string
  path: string
  icon: LucideIcon
  minimum?: UserRole
}

const tabs: TabDef[] = [
  { name: 'Home', shortName: 'Home', path: '/', icon: Home },
  { name: 'Work', shortName: 'Work', path: '/tasks', icon: ListChecks },
  { name: 'Orchestration', shortName: 'Orch', path: '/orchestration', icon: Layers, minimum: 'dev' },
  { name: 'Learning', shortName: 'Learn', path: '/learning', icon: GraduationCap },
  { name: 'News', shortName: 'News', path: '/news', icon: Newspaper },
  { name: 'Infrastructure', shortName: 'Infra', path: '/infrastructure', icon: Server, minimum: 'dev' },
]

// On mobile (<768px): show first 3 tabs in top nav, 4th in overflow menu
// On desktop: show all tabs normally
export function Tabs() {
  const [overflowOpen, setOverflowOpen] = useState(false)
  const overflowRef = useRef<HTMLDivElement>(null)
  const isMobile = useIsMobile()
  const isFullscreenActive = useMobileFullscreen((s) => s.isFullscreenActive)
  const { hasRole } = useAuth()

  const visibleTabs = tabs.filter((tab) => hasRole(tab.minimum ?? 'member'))

  // Close overflow menu when clicking outside — must be before any early return
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (overflowRef.current && !overflowRef.current.contains(e.target as Node)) {
        setOverflowOpen(false)
      }
    }
    if (overflowOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [overflowOpen])

  // Hide nav on mobile when a full-screen overlay is active
  if (isMobile && isFullscreenActive) {
    return null
  }

  // Mobile: first 3 visible tabs in top bar, rest in overflow menu
  const mobileMainTabs = visibleTabs.slice(0, 3)
  const overflowTabs = visibleTabs.slice(3)

  return (
    <div className="relative z-10 border-b border-white/5 bg-canvas">
      <nav className="flex items-center justify-between px-3 sm:px-6 md:px-8 pt-3 sm:pt-4" aria-label="Tabs">
        <div className="flex space-x-1 sm:space-x-6 md:space-x-8">
          {/* Desktop: show all permitted tabs */}
          <div className="hidden md:flex space-x-6 md:space-x-8">
            {visibleTabs.map((tab) => {
              const Icon = tab.icon
              return (
                <NavLink
                  key={tab.name}
                  to={tab.path}
                  className={({ isActive }) =>
                    `whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm transition-colors flex items-center gap-1.5
                    ${isActive
                      ? 'border-white text-white'
                      : 'border-transparent text-white/40 hover:text-white/60 hover:border-white/20'
                    }`
                  }
                >
                  <Icon size={14} />
                  <span>{tab.name}</span>
                </NavLink>
              )
            })}
          </div>

          {/* Mobile: show first 3 permitted tabs + overflow menu for rest */}
          <div className="flex md:hidden items-center space-x-1">
            {/* Portal target for route-specific leading elements (e.g., OrchestrationLayout hamburger) */}
            <div id="mobile-nav-leading" className="flex items-center" />
            {mobileMainTabs.map((tab) => {
              const Icon = tab.icon
              return (
                <NavLink
                  key={tab.name}
                  to={tab.path}
                  className={({ isActive }) =>
                    `min-w-[44px] min-h-[44px] flex flex-col items-center justify-center px-2 py-1 border-b-2 font-medium transition-colors
                    ${isActive
                      ? 'border-white text-white'
                      : 'border-transparent text-white/40 hover:text-white/60'
                    }`
                  }
                >
                  <Icon size={18} />
                  <span className="text-[10px] mt-0.5">{tab.shortName}</span>
                </NavLink>
              )
            })}

            {/* Overflow menu for remaining tabs */}
            {overflowTabs.length > 0 && (
              <div className="relative" ref={overflowRef}>
                <button
                  onClick={() => setOverflowOpen(!overflowOpen)}
                  className="min-w-[44px] min-h-[44px] flex flex-col items-center justify-center px-2 py-1 border-b-2 border-transparent text-white/40 hover:text-white/60 transition-colors"
                  aria-label="More navigation options"
                >
                  <MoreHorizontal size={18} />
                  <span className="text-[10px] mt-0.5">More</span>
                </button>

                {overflowOpen && (
                  <div className="absolute top-full right-0 mt-2 py-2 bg-[#1a1a1a] border border-white/10 rounded-lg shadow-xl z-50 min-w-[160px]">
                    {overflowTabs.map((tab) => {
                      const Icon = tab.icon
                      return (
                        <NavLink
                          key={tab.name}
                          to={tab.path}
                          onClick={() => setOverflowOpen(false)}
                          className={({ isActive }) =>
                            `flex items-center gap-3 px-4 py-3 text-sm transition-colors
                            ${isActive
                              ? 'text-white bg-white/10'
                              : 'text-white/60 hover:text-white hover:bg-white/5'
                            }`
                          }
                        >
                          <Icon size={16} />
                          <span>{tab.name}</span>
                        </NavLink>
                      )
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <NavLink
          to="/settings"
          className={({ isActive }) =>
            `min-w-[44px] min-h-[44px] p-2 flex items-center justify-center rounded-lg border transition-all duration-200
            ${isActive
              ? 'bg-white/10 border-white/10 text-white'
              : 'bg-white/5 hover:bg-white/10 border-white/5 hover:border-white/10 text-[#EEEEEE]/60 hover:text-[#EEEEEE]'
            }`
          }
          title="Settings"
        >
          <Settings size={16} />
        </NavLink>
      </nav>
    </div>
  )
}
